import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  getProvisionalAsset,
  listProvisionalAssets,
  patchProvisionalAsset,
  bulkValidateProvisionalAssets,
  bulkCreateProvisionalAssets,
  bulkExportProvisionalAssets
} from '../Api/ProvisionalAsset/ProvisionalAssetApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { isData, isObserver, User } from './User';
import { DrawerData } from './Drawer';
import { ValidatorResult } from '../components/Compounds/CardWithTitle';

export interface ProvisionalAsset {
  id: string;
  upstackId: string;
  accountId: string | null;
  tsd: string;
  salesAgent: string | null;
  commissionStatus: string | null;
  actualDecommissionedDate: string | null;
  disconnectDate: string | null;
  actualFirstCommissionRun: string | null;
  actualFirstSpiffRun: string | null;
  commissionableMrc: number | null;
  commissionableNrc: number | null;
  nonCommissionableNrc: number | null;
  installDate: string | null;
  cancelledDate: string | null;
  expectedDecommissionDate: string | null;
  tsdTrackingNumber: string | null;
  circuitId: string | null;
  commissionsValidatedDate: string | null;
  commissionsValidationStatus: string | null;
  commissionsValidationSubStatus: string | null;
  expectedGrossMrr: number | null;
  expectedGrossNrr: number | null;
  expectedNetMrr: number | null;
  expectedNetNrr: number | null;
  nonRecurringCommission: number | null;
  assetNumber: string | null;
  product2id: string | null;
  recurringCommission: number | null;
  supplier: string | null;
  supplierName: string | null;
  mrc: number | null;
  location: string | null;
  origin: string | null;
  fromId: string | null;
  toId: string | null;
  active: boolean;
  paymentFrequencyOverride: string | null;
  supplierPaymentFrequencyException: string | null;
  paymentFrequency: string | null;
  createdBy: User | null;
  createdAt: string | null;
  updatedAt: string | null;
  provisionalType: string | null;
  accountName: string | null;
  productName: string | null;
  tsdName: string | null;
  salesAgentName: string | null;
  commissionsExperienceAnalyst: string | null;
  commissionsExperienceAnalystEmail: string | null;
  parentCustomer: string | null;
  parentCustomerId: string | null;
  ultimateParent: string | null;
  ultimateParentId: string | null;
  rpmAccountCount?: string | null;
  quoteRepInvolved: boolean | null;
  quoteTeamSellingApprovalNumber: string | null;
  quoteTeamSellingDiscount: number | null;
  linkId: string | null;
  openCommissionsTicket: boolean;
  legacyArchiveReason: string | null;
  lastCommissionRun: string | null;
}

export type ProvisionalAssetUpdate = Partial<ProvisionalAsset> & Pick<ProvisionalAsset, 'id'>;

export interface wizardDataValidateRequest {
  rpmAccountIds: number[];
  assetMapping: Record<string, string>;
}

export interface wizardDataValidateResponse {
  customerErrors?: Record<string, string>[];
  errorCounts?: {
    invalid: number;
    valid: number;
    total: number;
  };
  otherErrors?: Record<string, string>[];
  preview?: Record<string, string>[];
  productErrors?: Record<string, string>[];
}

export interface wizardDataCreateResponse {
  success?: Record<string, string>[];
  failed?: Record<string, string>[];
}

export interface wizardData extends wizardDataValidateRequest, wizardDataValidateResponse, wizardDataCreateResponse {}

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const wizardDataSubject = new BehaviorSubject<wizardData>({ rpmAccountIds: [], assetMapping: {} });
const wizardDataLoadingSubject = new BehaviorSubject<boolean>(false);
const provisionalAssetSubject = new BehaviorSubject<ProvisionalAsset>({} as ProvisionalAsset);
const provisionalAssetDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const tallyProvisionalAssetsSubject = new BehaviorSubject<PaginatedResponse<ProvisionalAsset>>(
  {} as PaginatedResponse<ProvisionalAsset>
);
const provisionalAssetsByRpmAcctSub = new BehaviorSubject<PaginatedResponse<ProvisionalAsset>>(
  {} as PaginatedResponse<ProvisionalAsset>
);
const provisionalAssetsByRpmAcctDrawerSub = new BehaviorSubject<PaginatedResponse<ProvisionalAsset>>(
  {} as PaginatedResponse<ProvisionalAsset>
);
const provisionalAssetsByRpmAcctLoadingDrawerSubject = new BehaviorSubject<boolean>(false);

const provisionalAssetLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyProvisionalAssetsLoadingSubject = new BehaviorSubject<boolean>(false);
const provisionalAssetsByRpmAcctLoadingSubject = new BehaviorSubject<boolean>(false);
const updatingProvisionalAssetSubject = new BehaviorSubject<boolean>(false);
const paByTicketIdSubject = new BehaviorSubject<PaginatedResponse<ProvisionalAsset>>(
  {} as PaginatedResponse<ProvisionalAsset>
);
const paByTicketIdDrawerSubject = new BehaviorSubject<PaginatedResponse<ProvisionalAsset>>(
  {} as PaginatedResponse<ProvisionalAsset>
);
const paByTicketIdLoadingSubject = new BehaviorSubject<boolean>(false);
const paByTicketIdLoadingDrawerSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('provisional-asset', undefined);

export const getProvisionalAssetSubject = (): BehaviorSubject<ProvisionalAsset> => provisionalAssetSubject;
export const getProvisionalAssetLoadingSubject = (): BehaviorSubject<boolean> => provisionalAssetLoadingSubject;
export const getProvisionalAssetDetailSubject = (): BehaviorSubject<DrawerData | undefined> =>
  provisionalAssetDetailSubject;
export const getTallyProvisionalAssetsSubject = (): BehaviorSubject<PaginatedResponse<ProvisionalAsset>> =>
  tallyProvisionalAssetsSubject;
export const getTallyProvisionalAssetsLoadingSubject = (): BehaviorSubject<boolean> =>
  tallyProvisionalAssetsLoadingSubject;
export const getProvisionalAssetsByRpmAcctSub = (): BehaviorSubject<PaginatedResponse<ProvisionalAsset>> =>
  provisionalAssetsByRpmAcctSub;
export const getProvisionalAssetsByRpmAcctDrawerSub = (): BehaviorSubject<PaginatedResponse<ProvisionalAsset>> =>
  provisionalAssetsByRpmAcctDrawerSub;
export const getProvisionalAssetsByRpmAcctLoadingSubject = (): BehaviorSubject<boolean> =>
  provisionalAssetsByRpmAcctLoadingSubject;
export const getProvisionalAssetsByRpmAcctLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  provisionalAssetsByRpmAcctLoadingDrawerSubject;
export const updateProvisionalAssetDetail = (provisionalAssetData: ProvisionalAsset) =>
  provisionalAssetDetailSubject.next(provisionalAssetData);
export const getPAByTicketIdSubject = (): BehaviorSubject<PaginatedResponse<ProvisionalAsset>> => paByTicketIdSubject;
export const getPAByTicketIdDrawerSubject = (): BehaviorSubject<PaginatedResponse<ProvisionalAsset>> =>
  paByTicketIdDrawerSubject;
export const getPAByTicketIdLoadingSubject = (): BehaviorSubject<boolean> => paByTicketIdLoadingSubject;
export const getPAByTicketIdLoadingDrawerSubject = (): BehaviorSubject<boolean> => paByTicketIdLoadingDrawerSubject;
export const getWizardDataSubject = (): BehaviorSubject<wizardData> => wizardDataSubject;
export const getWizardDataLoadingSubject = (): BehaviorSubject<boolean> => wizardDataLoadingSubject;

export const getProvisionalAssetById = async (id: string): Promise<ProvisionalAsset> => {
  const asset = tallyProvisionalAssetsSubject?.getValue()?.data?.find((a) => a?.id === id);

  if (asset) {
    return asset;
  } else {
    const { data, error } = await getProvisionalAsset(id);
    if (error) addError('provisional-asset', error.message);
    if (data) return data;
  }

  return {} as ProvisionalAsset;
};

export const fetchProvisionalAsset = async (id: string, updateDrawer?: boolean): Promise<void | number> => {
  provisionalAssetLoadingSubject.next(true);

  const { data, error } = await getProvisionalAsset(id);

  if (data) {
    clearError();
    const subject = getProvisionalAssetSubject().getValue();
    if (!updateDrawer || !subject.id || subject.id === data.id) provisionalAssetSubject.next(data);
    const detailSubject = getProvisionalAssetDetailSubject().getValue() as ProvisionalAsset | undefined;
    if (updateDrawer || detailSubject?.id === data.id) provisionalAssetDetailSubject.next(data);
  }
  if (error) {
    addError('provisional-asset', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  provisionalAssetLoadingSubject.next(false);
};

export const fetchProvisionalAssets = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyProvisionalAssetsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyProvisionalAssetsLoadingSubject.next(true);

  const { data, error } = await listProvisionalAssets(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    tallyProvisionalAssetsSubject.next(data);
  }
  if (error) addError('provisional-asset', error.message);

  newFetchNumber === currentFetchNumber && tallyProvisionalAssetsLoadingSubject.next(false);
};

export const fetchProvisionalAssetsByRpmAcctId =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer
      ? provisionalAssetsByRpmAcctLoadingDrawerSubject
      : provisionalAssetsByRpmAcctLoadingSubject;
    const subject = forDrawer ? provisionalAssetsByRpmAcctDrawerSub : provisionalAssetsByRpmAcctSub;
    loading.next(true);

    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'assetNumber', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'rpmAccountId', operator: 'equals', value: id }]
    };
    const { data, error } = await listProvisionalAssets(mergedFilters);

    if (data) {
      clearError();
      subject.next(data);
    }
    if (error) addError('provisional-asset', error.message);

    loading.next(false);
  };

export const updateProvisionalAsset = async (asset: ProvisionalAssetUpdate, updateDrawer?: boolean) => {
  updatingProvisionalAssetSubject.next(true);

  const { data, error } = await patchProvisionalAsset(asset);

  if (data) {
    clearError();
    fetchProvisionalAsset(data.id, updateDrawer);
  }
  if (error) addError('provisional-asset', error.message);

  updatingProvisionalAssetSubject.next(false);
  return { data, error };
};

export const fetchPAByTicketId =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer ? paByTicketIdLoadingDrawerSubject : paByTicketIdLoadingSubject;
    const subject = forDrawer ? paByTicketIdDrawerSubject : paByTicketIdSubject;
    loading.next(true);
    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'assetNumber', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'ticketId', operator: 'equals', value: id }]
    };
    const { data, error } = await listProvisionalAssets(mergedFilters);

    if (error) addError('ticket', error.message);

    loading.next(false);

    if (data) {
      clearError();
      subject.next(data);
      return data;
    }
  };

export const handleCommissionsInfoValidation = (d: ProvisionalAsset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };

  if (isObserver() || isData() || !d) return result;

  if (d.commissionStatus === 'Decommissioned' && d.actualDecommissionedDate === null) {
    result.isValid = false;
    result.messages.push({ field: 'actualDecommissionedDate', message: 'Actual Decommission Run is required.' });
    result.invalidFields.push('actualDecommissionedDate');
  }

  return result;
};

export const handleRpmAccountValidation = (d: ProvisionalAsset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };
  if (isObserver() || isData() || !d) return result;

  if (
    (d.commissionStatus === 'Commissioning' || d.commissionStatus === 'Partially Commissioning') &&
    (d.rpmAccountCount === '0' || d.rpmAccountCount === null)
  ) {
    result.isValid = false;
    result.messages.push({ field: 'rpmAccountCount', message: 'Link to RPM Account is required.' });
    result.invalidFields.push('rpmAccountCount');
  }

  return result;
};

export const handleValidationStatusValidation = (d: ProvisionalAsset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };

  if (isObserver() || isData() || !d) return result;

  if (
    (d.commissionStatus === 'Commissioning' || d.commissionStatus === 'Partially Commissioning') &&
    d.commissionsValidationStatus === null
  ) {
    result.isValid = false;
    result.messages.push({ field: 'commissionsValidationStatus', message: 'Validation Status is required.' });
    result.invalidFields.push('commissionsValidationStatus');
  }

  return result;
};

export const updateWizardData = (wizardData: wizardData) => wizardDataSubject.next(wizardData);

export const bulkValidate = async () => {
  const wizardData = wizardDataSubject.getValue();
  const dataValidate = { assetMapping: wizardData.assetMapping, rpmAccountIds: wizardData.rpmAccountIds };
  wizardDataLoadingSubject.next(true);
  const { data, error } = await bulkValidateProvisionalAssets(dataValidate);

  if (data) {
    wizardDataSubject.next({ ...wizardData, ...data });
    wizardDataLoadingSubject.next(false);
  }
  if (error) {
    wizardDataLoadingSubject.next(false);
    addError('provisional-asset', error.message);
  }
};

export const bulkCreate = async () => {
  const wizardData = wizardDataSubject.getValue();
  const dataValidate = wizardData.preview || [];
  if (dataValidate.length) {
    wizardDataLoadingSubject.next(true);
    const { data, error } = await bulkCreateProvisionalAssets(dataValidate);

    if (data) {
      wizardDataSubject.next({ ...wizardData, success: data });
      wizardDataLoadingSubject.next(false);
    }
    if (error) {
      wizardDataLoadingSubject.next(false);
      addError('provisional-asset', error.message);
    }
  }
};

export const bulkExport = async () => {
  const wizardData = wizardDataSubject.getValue();
  const dataExport = {
    customerErrors: wizardData.customerErrors || [],
    otherErrors: wizardData.otherErrors || [],
    productErrors: wizardData.productErrors || []
  };
  if (dataExport.customerErrors.length || dataExport.otherErrors.length || dataExport.productErrors.length) {
    await bulkExportProvisionalAssets(dataExport);
  }
};
